import React from 'react';
import { Table, Input, InputNumber, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FG163 } from '../../../services/pwg208Api';

interface Tab3PrepaymentProps {
  prepayments: FG163[];
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onUpdate: <K extends keyof FG163>(idx: number, key: K, value: FG163[K]) => void;
}

const Tab3Prepayments: React.FC<Tab3PrepaymentProps> = React.memo(({
  prepayments,
  isEditing,
  onAdd,
  onDelete,
  onUpdate,
}) => {
  const columns: ColumnsType<FG163> = [
    {
      title: <span data-contract-field="fg163_02">序號</span>,
      dataIndex: 'fg163_02',
      key: 'fg163_02',
      width: 60,
      align: 'center',
    },
    {
      title: <span data-contract-field="fg163_10">預收款單號</span>,
      dataIndex: 'fg163_10',
      key: 'fg163_10',
      width: 160,
      render: (v: string, _record: FG163, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => onUpdate(idx, 'fg163_10', e.target.value)}
            maxLength={20}
          />
        ) : v,
    },
    {
      title: <span data-contract-field="fg163_11">S/C No</span>,
      dataIndex: 'fg163_11',
      key: 'fg163_11',
      width: 140,
      render: (v: string, _record: FG163, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            onChange={(e) => onUpdate(idx, 'fg163_11', e.target.value)}
            maxLength={20}
          />
        ) : v,
    },
    {
      title: <span data-contract-field="fg163_12">預收款金額(CJ)</span>,
      dataIndex: 'fg163_12',
      key: 'fg163_12',
      width: 130,
      align: 'right',
      render: (v: number, _record: FG163, idx: number) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            precision={2}
            onChange={(val) => onUpdate(idx, 'fg163_12', val ?? 0)}
            style={{ width: '100%' }}
          />
        ) : (
          v?.toLocaleString()
        ),
    },
    {
      title: <span data-contract-field="fg163_13">預收款金額(CF)</span>,
      dataIndex: 'fg163_13',
      key: 'fg163_13',
      width: 130,
      align: 'right',
      render: (v: number, _record: FG163, idx: number) =>
        isEditing ? (
          <InputNumber
            size="small"
            value={v}
            precision={2}
            onChange={(val) => onUpdate(idx, 'fg163_13', val ?? 0)}
            style={{ width: '100%' }}
          />
        ) : (
          v?.toLocaleString()
        ),
    },
    ...(isEditing
      ? [
          {
            title: '',
            key: 'actions',
            width: 40,
            render: (_: unknown, _record: FG163, idx: number) => (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(idx)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="pwg208-tab3">
      {isEditing && (
        <div className="tab-toolbar">
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={onAdd}
            type="dashed"
          >
            新增預收款
          </Button>
        </div>
      )}
      <Table<FG163>
        columns={columns}
        dataSource={prepayments}
        rowKey={(r) => `${r.fg163_01}-${r.fg163_02}`}
        pagination={false}
        size="small"
        scroll={{ y: 300 }}
        locale={{ emptyText: '無預收款資料' }}
      />
      <div className="tab-summary">
        共 <strong>{prepayments.length}</strong> 筆
      </div>
    </div>
  );
});

Tab3Prepayments.displayName = 'Tab3Prepayments';

export default Tab3Prepayments;
